/**
 * Common date parsing and validation logic
 */
const parseDateSafely = (date: Date | null | undefined): Date | null => {
    if (!date) {
        return null;
    }

    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
        return null;
    }

    return dateObj;
};

/**
 * Get common date/time components
 */
const getDateTimeComponents = (dateObj: Date) => {
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');

    return { hours, minutes, seconds };
};

/**
 * Formats a date to match the design: "MM/dd/yyyy - HH:mm:ss.SSS"
 * Example: "09/23/2024 - 21:06:56.791"
 */
export const formatTelecomDate = (date: Date | null | undefined): string => {
    const dateObj = parseDateSafely(date);
    if (!dateObj) {
        return '-';
    }

    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();

    const { hours, minutes, seconds } = getDateTimeComponents(dateObj);
    const milliseconds = String(dateObj.getMilliseconds()).padStart(3, '0');

    return `${month}/${day}/${year} - ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

/**
 * Formats a date with timezone information to match Figma design
 * Example: "Thu Jul 22 2024 - 16:32:28 GMT - 0700 (Pacific Daylight Time)"
 */
export const formatTelecomDateWithTimezone = (date: Date | null | undefined): string => {
    const dateObj = parseDateSafely(date);
    if (!dateObj) {
        return '-';
    }

    // Get day name (Thu)
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });

    // Get month name (Jul)
    const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });

    // Get day, year
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();

    // Get time components
    const { hours, minutes, seconds } = getDateTimeComponents(dateObj);

    // Get timezone offset and name
    const timeZoneOffset = dateObj.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timeZoneOffset) / 60);
    const offsetMinutes = Math.abs(timeZoneOffset) % 60;
    const offsetSign = timeZoneOffset <= 0 ? '+' : '-';
    const gmtOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}${String(offsetMinutes).padStart(2, '0')}`;

    // Get timezone name for this specific date (correctly shows Standard vs Daylight time)
    const timeZoneName =
        new Intl.DateTimeFormat('en-US', {
            timeZoneName: 'long',
        })
            .formatToParts(dateObj)
            .find(part => part.type === 'timeZoneName')?.value || '';

    return `${dayName} ${monthName} ${day} ${year} - ${hours}:${minutes}:${seconds} GMT ${offsetSign} ${gmtOffset.slice(1)} (${timeZoneName})`;
};
